import { useCallback, useEffect, useState } from "react";
import ApiErrorMessages from "../components/UI/ApiErrorMessages";
import Button from "../components/UI/Button";
import Loader from "../components/UI/Loader";
import Modal from "../components/UI/Modal";
import useElementVisible from "../hooks/useElementVisible";
import useSortableData from "../hooks/useSortableData";
import DataTable from "../components/UI/DataTable";
import { DataTableColumn } from "../models/DataTable";
import { sortableBulkParticipantManagementEnabled } from "../utils/features";
import {
    BulkActionBatch,
    BulkActionBatchItem,
    getBulkParticipantActionBatch,
    getBulkParticipantActionBatches,
    getBulkParticipantActionBatchItems
} from "../services/clientsService";
import "./BulkParticipantManagement.css";

const TAKE = 50;
const BATCH_ITEM_TAKE = 100;

const STATUS_DISPLAY_NAMES: Record<string, string> = {
    pending: "Pending",
    in_progress: "In Progress",
    cancel_requested: "Cancel Requested",
    cancelled: "Cancelled",
    completed: "Completed",
    completed_with_errors: "Completed with Errors",
    failed: "Failed",
    succeeded: "Succeeded"
};

const formatStatus = (status?: string | null) => {
    if (!status) {
        return "";
    }

    return STATUS_DISPLAY_NAMES[status] || status
        .split("_")
        .map(part => part.charAt(0).toUpperCase() + part.slice(1))
        .join(" ");
};

const formatDateTime = (value?: string | null, fallback = "") => {
    if (!value) {
        return fallback;
    }

    const date = new Date(value);
    if (!Number.isNaN(date.getTime())) {
        return date.toLocaleString(undefined, {
            year: "numeric",
            month: "2-digit",
            day: "2-digit",
            hour: "numeric",
            minute: "2-digit"
        });
    }

    return value.slice(0, 16).replace("T", " ");
};

const formatUserName = (batch: BulkActionBatch) => {
    return [batch.createdByFirstName, batch.createdByLastName].filter(Boolean).join(" ") || batch.createdBy;
};

const BulkParticipantActionHistory = () => {
    const [actions, setActions] = useState<BulkActionBatch[]>([]);
    const [loading, setLoading] = useState(true);
    const [paging, setPaging] = useState(false);
    const [skip, setSkip] = useState(0);
    const [hasMore, setHasMore] = useState(true);
    const [apiError, setApiError] = useState<any>();
    const [activeAction, setActiveAction] = useState<BulkActionBatch>();
    const [actionItems, setActionItems] = useState<BulkActionBatchItem[]>([]);
    const [actionItemSkip, setActionItemSkip] = useState(0);
    const [actionItemsPaging, setActionItemsPaging] = useState(false);
    const [detailsLoadingId, setDetailsLoadingId] = useState<string>();
    const [detailsVisible, setDetailsVisible] = useState(false);
    const [pagingReference, pagingVisible] = useElementVisible<HTMLDivElement>();
    const [actionItemPagingReference, actionItemPagingVisible] = useElementVisible<HTMLDivElement>();
    const hasMoreActionItems = !!activeAction && actionItems.length < activeAction.totalCount;
    const getActionSortValue = useCallback((action: BulkActionBatch, key: string) => {
        if (key === 'ranBy') return formatUserName(action);
        return action[key as keyof BulkActionBatch];
    }, []);
    const getActionItemSortValue = useCallback((item: BulkActionBatchItem, key: string) => item[key as keyof BulkActionBatchItem], []);
    const { data: sortedActions, sortBy: actionSortBy, sortDirection: actionSortDirection, onSort: onActionSort } = useSortableData(actions, getActionSortValue);
    const { data: sortedActionItems, sortBy: actionItemSortBy, sortDirection: actionItemSortDirection, onSort: onActionItemSort } = useSortableData(actionItems, getActionItemSortValue);

    useEffect(() => {
        document.title = "Action History";
        loadActions(0, false);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        if (skip === 0) {
            return;
        }

        loadActions(skip, true);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [skip]);

    useEffect(() => {
        if (pagingVisible && hasMore && !loading && !paging) {
            setSkip(current => current + TAKE);
        }
    }, [pagingVisible, hasMore, loading, paging]);

    useEffect(() => {
        if (actionItemSkip === 0 || !activeAction) {
            return;
        }

        loadActionItems(activeAction.id, actionItemSkip, true);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [actionItemSkip]);

    useEffect(() => {
        if (actionItemPagingVisible && detailsVisible && hasMoreActionItems && !actionItemsPaging) {
            setActionItemSkip(current => current + BATCH_ITEM_TAKE);
        }
    }, [actionItemPagingVisible, detailsVisible, hasMoreActionItems, actionItemsPaging]);

    const loadActions = async (nextSkip: number, append: boolean) => {
        if (append) {
            setPaging(true);
        } else {
            setLoading(true);
        }
        setApiError(undefined);

        try {
            const result = await getBulkParticipantActionBatches(nextSkip, TAKE);
            setActions(current => append ? [...current, ...result] : result);
            setHasMore(result.length === TAKE);
        } catch (e) {
            console.error(e);
            setApiError(e);
        } finally {
            setLoading(false);
            setPaging(false);
        }
    };

    const loadActionItems = async (batchId: string, nextSkip: number, append: boolean) => {
        setActionItemsPaging(true);
        setApiError(undefined);

        try {
            const items = await getBulkParticipantActionBatchItems(batchId, nextSkip, BATCH_ITEM_TAKE);
            setActionItems(current => append ? [...current, ...items] : items);
            return items;
        } catch (e) {
            console.error(e);
            setApiError(e);
        } finally {
            setActionItemsPaging(false);
        }
    };

    const handleViewDetails = async (batchId: string) => {
        setDetailsLoadingId(batchId);
        setApiError(undefined);

        try {
            const batch = await getBulkParticipantActionBatch(batchId);
            setActiveAction(batch);
            setActionItems([]);
            setActionItemSkip(0);
            const items = await loadActionItems(batchId, 0, false);
            if (!items) {
                setActionItems([]);
            }
            setDetailsVisible(true);
        } catch (e) {
            console.error(e);
            setApiError(e);
        } finally {
            setDetailsLoadingId(undefined);
        }
    };

    return (
        <section id="main-content" tabIndex={-1}>
            <div className="section">
                <div className="bulk-management-shell">
                    <div className="bulk-history-back-row">
                        <Button id="back-to-inactive-participants"
                            text="Back"
                            href="/bulk-management/participants"
                            icon={<i className="ri-arrow-left-s-line" />}
                            iconLocation="start"
                            className="small secondary hide-on-print" />
                    </div>
                    <h1>Action History</h1>
                    <ApiErrorMessages error={apiError} />
                    {loading && <Loader />}
                    {!loading && actions.length === 0 && <div className="bulk-empty">No bulk actions found</div>}
                    {actions.length > 0 && <div className="bulk-table-scroll">
                        <DataTable
                            columns={[
                                { key: 'created', label: 'Created', sortable: true },
                                { key: 'ranBy', label: 'Ran By', sortable: true },
                                { key: 'started', label: 'Started', sortable: true },
                                { key: 'completed', label: 'Stopped', sortable: true },
                                { key: 'status', label: 'Status', sortable: true },
                                { key: 'processedCount', label: 'Progress', sortable: true },
                                { key: 'successCount', label: 'Succeeded', sortable: true },
                                { key: 'failedCount', label: 'Failed', sortable: true },
                                { key: 'cancelledCount', label: 'Cancelled', sortable: true },
                                { key: 'actions', label: '', sortable: false }
                            ] as DataTableColumn[]}
                            data={sortedActions}
                            sortBy={actionSortBy}
                            sortDirection={actionSortDirection}
                            onSort={onActionSort}
                            sortingEnabled={sortableBulkParticipantManagementEnabled}
                            renderRow={action => <tr key={action.id} className="table-row">
                                    <td>{formatDateTime(action.created)}</td>
                                    <td>{formatUserName(action)}</td>
                                    <td>{formatDateTime(action.started, "Not started")}</td>
                                    <td>{formatDateTime(action.completed, "Not stopped")}</td>
                                    <td>{formatStatus(action.status)}</td>
                                    <td>{action.processedCount} of {action.totalCount}</td>
                                    <td>{action.successCount}</td>
                                    <td>{action.failedCount}</td>
                                    <td>{action.cancelledCount}</td>
                                    <td>
                                        <div className="bulk-table-actions">
                                            <Button id={`view-action-history-${action.id}`}
                                                className="secondary small"
                                                text="View"
                                                loading={detailsLoadingId === action.id}
                                                disabled={detailsLoadingId !== undefined}
                                                onClick={() => handleViewDetails(action.id)} />
                                        </div>
                                    </td>
                                </tr>} />
                    </div>}
                    {paging && <Loader />}
                    {hasMore && <div ref={pagingReference} className="bulk-scroll-sentinel"></div>}
                    <Modal id="bulk-history-details-modal"
                        visible={detailsVisible && activeAction !== undefined}
                        className="medium"
                        onClose={() => setDetailsVisible(false)}
                        title="Action Details">
                        {activeAction && <div>
                            <div className="bulk-status-header">
                                <h2>Action Status</h2>
                            </div>
                            <div className="bulk-metrics">
                                <div><span>Created</span><strong>{formatDateTime(activeAction.created)}</strong></div>
                                <div><span>Status</span><strong>{formatStatus(activeAction.status)}</strong></div>
                                <div><span>Total</span><strong>{activeAction.totalCount}</strong></div>
                                <div><span>Succeeded</span><strong>{activeAction.successCount}</strong></div>
                                <div><span>Failed</span><strong>{activeAction.failedCount}</strong></div>
                                <div><span>Cancelled</span><strong>{activeAction.cancelledCount}</strong></div>
                            </div>
                            {actionItems.length > 0 && <div className="bulk-table-scroll">
                                <DataTable
                                    columns={[
                                        { key: 'clientName', label: 'Participant', sortable: true },
                                        { key: 'status', label: 'Status', sortable: true },
                                        { key: 'errorMessage', label: 'Error', sortable: true }
                                    ] as DataTableColumn[]}
                                    data={sortedActionItems}
                                    sortBy={actionItemSortBy}
                                    sortDirection={actionItemSortDirection}
                                    onSort={onActionItemSort}
                                    sortingEnabled={sortableBulkParticipantManagementEnabled}
                                    renderRow={item => <tr key={item.id} className="table-row">
                                            <td>{item.clientName}</td>
                                            <td>{formatStatus(item.status)}</td>
                                            <td>{item.errorMessage || ""}</td>
                                        </tr>} />
                            </div>}
                            {actionItemsPaging && <Loader />}
                            {hasMoreActionItems && <div ref={actionItemPagingReference} className="bulk-scroll-sentinel"></div>}
                        </div>}
                    </Modal>
                </div>
            </div>
        </section>
    );
};

export default BulkParticipantActionHistory;
